import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Star, ArrowLeft } from "lucide-react";
import { Pagination } from "../../components/client";
import FilterBar from "../../components/shared/FilterBar";
import { useBrowseVendors } from "../../hooks/useBrowseVendors";

const BrowseVendors = () => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  const {
    // State
    searchTerm,
    setSearchTerm,
    location,
    setLocation,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    selectedPriceRange,
    setSelectedPriceRange,
    selectedRating,
    setSelectedRating,
    sortBy,
    setSortBy,
    setCurrentPage,
    hoveredVendor,
    setHoveredVendor,
    
    // Data
    vendors: sortedVendors,
    pagination,
    loading,
    
    // Actions
    clearAllFilters,
  } = useBrowseVendors();

  const sortOptions = [
    { value: "none", label: "No Sorting" },
    { value: "rating", label: "Highest Rated" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
    { value: "reviews", label: "Most Reviews" },
    { value: "distance", label: "Distance" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile: Title Section */}
      <div className="bg-white border-b border-gray-200 md:hidden">
        <div className="page-container py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="text-sm">Back</span>
            </Link>
            <h1 className="text-lg font-bold text-gray-900 text-center flex-1 mx-4">
              Browse Vendors
            </h1>
            <div className="w-12"></div>
          </div>
        </div>
      </div>
              
      {/* Mobile: Sticky Filter Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm md:hidden">
        <div className="page-container py-3">
          <FilterBar
            selectedPriceRange={selectedPriceRange}
            setSelectedPriceRange={setSelectedPriceRange}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            location={location}
            setLocation={setLocation}
            selectedCategory=""
            setSelectedCategory={() => {}}
            categories={[]}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOptions={sortOptions}
            clearAllFilters={clearAllFilters}
            showCategoryFilter={false}
            priceMin={priceMin}
            setPriceMin={setPriceMin}
            priceMax={priceMax}
            setPriceMax={setPriceMax}
          />
        </div>
      </div>

      {/* Desktop: One Line Header - Back | Filters | Count (Sticky) */}
      <div className="hidden md:block bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="page-container py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Back Button */}
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-black transition-colors whitespace-nowrap"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-base">Back</span>
            </Link>

            <div className="flex-1"></div>

            {/* Filter Bar on Right */}
            <div className="flex items-center gap-4">
              <FilterBar
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                showMoreFilters={showMoreFilters}
                setShowMoreFilters={setShowMoreFilters}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                location={location}
                setLocation={setLocation}
                selectedCategory=""
                setSelectedCategory={() => {}}
                categories={[]}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOptions={sortOptions}
                clearAllFilters={clearAllFilters}
                showCategoryFilter={false}
                priceMin={priceMin}
                setPriceMin={setPriceMin}
                priceMax={priceMax}
                setPriceMax={setPriceMax}
              />

              {/* Results Count */}
              <p className="text-sm text-gray-600 whitespace-nowrap">
                {loading ? "Loading..." : `${sortedVendors.length} vendors`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-4 md:py-8 md:px-8 px-8">
        {/* Mobile Results Count */}
        <p className="text-sm text-gray-600 mb-4 md:hidden">
          {loading ? "Loading..." : `${sortedVendors.length} vendors found`}
        </p>

        {/* Desktop: Title next to cards */}
        <h2 className="hidden md:block text-2xl font-bold text-gray-900 mb-6">
          Browse Vendors
        </h2>

        <div className="flex gap-8 relative">
          {/* Services Grid */}
          <div className="flex-1">
            {/* Vendor Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div
                    key={i}
                    className="bg-white rounded-xl md:rounded-2xl overflow-hidden border border-gray-200 animate-pulse"
                  >
                    <div className="h-52 bg-gray-200"></div>
                    <div className="p-3 md:p-4">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-3"></div>
                      <div className="h-3 bg-gray-200 rounded mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-4">
                {sortedVendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    onMouseEnter={() => setHoveredVendor(vendor)}
                    onMouseLeave={() => setHoveredVendor(null)}
                    className="relative"
                  >
                    <Link
                      to={`/vendor/${vendor.id}`}
                      className="block group bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200 md:border-transparent md:hover:border-gray-200"
                    >
                    {/* Image */}
                    <div className="relative h-52 md:h-52 overflow-hidden">
                      {vendor.image ? (
                        <img
                          src={vendor.image}
                          alt={vendor.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                          <span className="text-gray-500 text-sm">
                            Vendor Image
                          </span>
                        </div>
                      )}

                      {/* Featured Badge */}
                    {vendor.featured && (
                        <div className="absolute top-2 md:top-3 left-2 md:left-3 bg-black text-white px-2 md:px-3 py-1 rounded-full text-xs font-medium">
                        Featured
                      </div>
                    )}
                    
                      {/* Heart Icon */}
                      {/* <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="absolute top-2 md:top-3 right-2 md:right-3 p-1.5 md:p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors md:opacity-0 md:group-hover:opacity-100"
                        aria-label="Add to favorites"
                      >
                        <Heart className="w-4 h-4 text-gray-700" />
                      </button> */}
                    </div>
                    
                    {/* Content */}
                    <div className="p-3 md:p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm md:text-sm font-semibold text-gray-900 line-clamp-2 flex-1">
                          {vendor.name}
                        </h3>
                        <div className="flex items-center ml-2 flex-shrink-0">
                          <Star className="w-3 h-3 fill-current text-gray-900" />
                          <span className="text-xs font-medium text-gray-900 ml-1">
                            {vendor.rating.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 mb-1">
                        {vendor.category}
                      </p>

                      <div className="flex items-center text-xs text-gray-500 mb-2 md:mb-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        {vendor.location || "Location not specified"}
                      </div>
                      
                      <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-gray-100">
                        <div>
                          <span className="text-sm font-bold text-gray-900">
                            {vendor.serviceCount} {vendor.serviceCount === 1 ? 'Service' : 'Services'}
                              </span>
                        </div>
                        {vendor.reviewCount > 0 && (
                          <span className="text-xs text-gray-500 hidden sm:inline">
                            {vendor.reviewCount} reviews
                        </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  </div>
                ))}
              </div>
            )}

            {/* Side Detail Panel - Shows on Hover (Desktop Only) */}
            {hoveredVendor && (
              <div className="hidden xl:block fixed right-8 top-1/4 w-96 z-30">
                <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 animate-fade-in">
                  <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                    {hoveredVendor.image ? (
                      <img src={hoveredVendor.image} alt={hoveredVendor.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 text-sm">Vendor Image</span>
                      </div>
                    )}
                    {hoveredVendor.featured && (
                      <div className="absolute top-3 left-3 bg-black text-white px-3 py-1 rounded-full text-xs font-medium">
                        Featured
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {hoveredVendor.name}
                  </h3>

                  <p className="text-sm text-gray-600 mb-3">{hoveredVendor.category}</p>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 fill-current text-gray-900 mr-1" />
                      <span className="font-semibold">{hoveredVendor.rating.toFixed(2)}</span>
                      <span className="text-gray-500 text-sm ml-1">
                        ({hoveredVendor.reviewCount} reviews)
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-4 h-4 mr-2" />
                    {hoveredVendor.location || 'Location not specified'}
                  </div>

                  {hoveredVendor.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {hoveredVendor.description}
                    </p>
                  )}

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-baseline justify-between mb-4">
                      <div>
                        <span className="text-2xl font-bold text-gray-900">
                          {hoveredVendor.serviceCount}
                        </span>
                        <span className="text-gray-600 text-sm ml-2">
                          {hoveredVendor.serviceCount === 1 ? 'Service' : 'Services'}
                        </span>
                      </div>
                    </div>
                    
                    <Link
                      to={`/vendor/${hoveredVendor.id}`}
                      className="block w-full py-3 bg-black text-white text-center rounded-xl font-light hover:bg-gray-800 transition-colors"
                    >
                      View Vendor Profile
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Pagination */}
            {!loading && sortedVendors.length > 0 && (
              <Pagination
                currentPage={pagination.current_page}
                lastPage={pagination.last_page}
                perPage={pagination.per_page}
                total={pagination.total}
                onPageChange={setCurrentPage}
              />
            )}

            {/* No Results - Only show when location detection and data fetching are complete */}
            {!loading && sortedVendors.length === 0 && (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No vendors found
                </h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your search criteria or filters
                </p>
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowseVendors;

